import type { Action } from "@/types";
import { resources, groups, Resource } from "./resources";
import { LifeBuoy, Send } from "lucide-react";

export interface SubNavItem {
  title: string;
  url: string;
  permission?: {
    resource: Resource;
    action: Action;
  };
}

export interface NavItem {
  title: string;
  icon: React.ComponentType;
  url?: string;
  items?: SubNavItem[];
  permission?: {
    resource: Resource;
    action: Action;
  };
}

interface DashboardNav {
  navMain: NavItem[];
  role: NavItem[];
  management: NavItem[];
  help: NavItem[];
}

// Helper function to create a nav item from a resource
const createNavItem = (resource: Resource): NavItem => {
  const metadata = resources[resource];
  const navItem: NavItem = {
    title: metadata.name,
    url: metadata.url,
    icon: metadata.icon!,
  };

  // Add permission requirement unless explicitly specified in navigation
  if (metadata.navigation?.section) {
    navItem.permission = {
      resource,
      action: "read" as Action,
    };
  }

  return navItem;
};

// Helper function to map URLs to resource names
const getResourceFromUrl = (url: string): Resource | null => {
  // Find the resource that matches this URL
  const resourceEntry = Object.entries(resources).find(
    ([, metadata]) => metadata.url === url
  );
  return resourceEntry ? (resourceEntry[0] as Resource) : null;
};

// Helper function to create a nav item from a group
const createGroupNavItem = (groupKey: keyof typeof groups): NavItem => {
  const group = groups[groupKey];
  return {
    title: group.name,
    url: group.defaultUrl,
    icon: group.icon,
    permission: {
      resource: groupKey as Resource,
      action: "read" as Action,
    },
    items: group.subItems?.map((item) => {
      const resourceForUrl = getResourceFromUrl(item.url);
      return {
        title: item.title,
        url: item.url,
        ...(item.requiresPermission &&
          resourceForUrl && {
            permission: {
              resource: resourceForUrl,
              action: "read" as Action,
            },
          }),
      };
    }),
  };
};

// Helper function to group resources by section
const groupResourcesBySection = (section: "role" | "management") => {
  return Object.entries(resources)
    .filter(([, metadata]) => metadata.navigation?.section === section)
    .reduce<Record<string, Resource[]>>((acc, [key, metadata]) => {
      const group = metadata.navigation?.group || "default";
      if (!acc[group]) acc[group] = [];
      acc[group].push(key as Resource);
      return acc;
    }, {});
};

export const dashboardNav: DashboardNav = {
  navMain: [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: resources.analytics.icon!,
    },
  ],
  role: Object.entries(groupResourcesBySection("role")).flatMap(
    ([group, resourceKeys]) =>
      group === "default"
        ? resourceKeys.map((key) => createNavItem(key))
        : [
            {
              ...createGroupNavItem(group as keyof typeof groups),
              items: [
                ...(groups[group as keyof typeof groups].subItems || []).map(
                  (item) => {
                    const resourceForUrl = getResourceFromUrl(item.url);
                    return {
                      title: item.title,
                      url: item.url,
                      ...(item.requiresPermission &&
                        resourceForUrl && {
                          permission: {
                            resource: resourceForUrl,
                            action: "read" as Action,
                          },
                        }),
                    };
                  }
                ),
              ],
            },
          ]
  ),
  management: Object.entries(groupResourcesBySection("management")).flatMap(
    ([group, resourceKeys]) =>
      group === "default"
        ? resourceKeys.map((key) => createNavItem(key))
        : [
            {
              ...createGroupNavItem(group as keyof typeof groups),
              items: [
                ...(groups[group as keyof typeof groups].subItems || []).map(
                  (item) => {
                    const resourceForUrl = getResourceFromUrl(item.url);
                    return {
                      title: item.title,
                      url: item.url,
                      ...(item.requiresPermission &&
                        resourceForUrl && {
                          permission: {
                            resource: resourceForUrl,
                            action: "read" as Action,
                          },
                        }),
                    };
                  }
                ),
              ],
            },
          ]
  ),
  help: [
    {
      title: "Support",
      url: "/dashboard/support",
      icon: LifeBuoy,
    },
    {
      title: "Feedback",
      url: "/dashboard/feedback",
      icon: Send,
    },
  ],
};
